
const network = require( './network/network_api' );
import { FINISHING_STATES } from "./slot";
import Autoplay from "./autoplay";
import Stream from "./frp/stream";

export default function(
    parent,
    $credits,
    config,
    leaf,
)
{
    const space = parent.child( "ui" );
    
    $credits.subscribe( leaf.s( "credits" ) );
    
    const $state = parent.s( "state" );
    
    const $userBet = space.s( "userBet" ).description( "Last user inputted bet or retrieved from server. Used to restore bet." );
    const $resultBet = space.s( "resultBet" ).description( "What bet player currently sees." );
    
    //cannot use $resultBet directly because polling tries to reset bet back to server's value:
    $state
        .take( 1 )
        .map( state => state.server.bet )
        .to( $userBet );
    
    $state
        .any(
            $credits,
            leaf.s( "canBet" ),
            $userBet,
            ( state, _, canBet, userBet ) => ({ state, canBet, userBet })
        )
        .with( $resultBet, ( { state, canBet, userBet }, resultBet ) => ({ state, canBet, userBet, resultBet }) )
        .map( ({ state, canBet, userBet, resultBet }) => ({
            bet : canBet ? state.server.shrink( userBet ) : ( resultBet ? resultBet.bet : state.server.bet ),
            state
        }) )
        .subscribe( $resultBet );
    $resultBet
        .map( c => c.state.server.betToValue( c.bet ) )
        .subscribe( leaf.s( "betValue" ) );
    $resultBet
        .map( c => c.state.server.betToValue( c.bet ).lines )
        .subscribe( leaf.s( "linesValue" ) );
    $resultBet
        .map( c => c.state.server.betToValue( c.bet ).multiplier )
        .subscribe( leaf.s( "multiplierValue" ) );
    $resultBet
        .map( c => c.state.server.price( c.bet ) )
        .subscribe( leaf.s( "price" ) );
    leaf.s( 'price' )
        .with( parent.s( 'state' ), ( price, state ) => price * state.state.credit_price )
        .name( leaf, 'money_price' )
    $resultBet
        .map( c => ! c.state.server.cannotPay( c.bet ) )
        .subscribe( leaf.s( "canPay" ) );
    
    leaf.s( "increaseLines" )
        .withLatestFrom( $resultBet, ( intent, c ) => c )
        .subscribe( ({ bet, state }) =>
        {
            const r = {
                lines : bet.lines < config.lines.length - 1 ? bet.lines + 1 : 0,
                multiplier : bet.multiplier,
            };
            if ( state.server.cannotPay( r ) ) r.lines = 0;

            $userBet.next( state.server.shrink( r ) );
        } );
    
    leaf.s( "decreaseLines" )
        .withLatestFrom( $resultBet, ( intent, c ) => c )
        .subscribe( ({ bet, state }) =>
        {
            const r = {
                lines : bet.lines > 0 ? bet.lines - 1 : config.lines.length - 1,
                multiplier : bet.multiplier,
            };
            while ( state.server.cannotPay( r ) && r.lines > 0 ) -- r.lines;

            $userBet.next( state.server.shrink( r ) );
        } );
    
    leaf.s( "increaseMultiplier" )
        .withLatestFrom( $resultBet, ( intent, c ) => c )
        .subscribe( ({ bet, state }) =>
        {
            const r = {
                lines : bet.lines,
                multiplier : bet.multiplier < config.multipliers.length - 1 ? bet.multiplier + 1 : 0,
            };
            if ( state.server.cannotPay( r ) ) r.multiplier = 0;

            $userBet.next( state.server.shrink( r ) );
        } );
    
    leaf.s( "decreaseMultiplier" )
        .withLatestFrom( $resultBet, ( intent, c ) => c )
        .subscribe( ({ bet, state }) =>
        {
            const r = {
                lines : bet.lines,
                multiplier : bet.multiplier > 0 ? bet.multiplier - 1 : config.multipliers.length - 1,
            };
            while ( state.server.cannotPay( r ) && r.multiplier > 0 ) -- r.multiplier;

            $userBet.next( state.server.shrink( r ) );
        } );
    
    leaf.s( "setLines" )
        .withLatestFrom( $resultBet, ( intent, c ) => ({ intent, bet : c.bet, state : c.state }) )
        .subscribe( ({ intent, bet, state }) =>
        {
            const r = {
                lines : intent,
                multiplier : bet.multiplier,
            };
            
            while ( state.server.cannotPay( r ) && r.lines > 0 ) -- r.lines;

            $userBet.next( state.server.shrink( r ) );
        } );
    
    Stream.any(
        parent.s( "net" ),
        parent.s( "animating" ),
        parent.s( "pending" ),
        ( net, animating, pending ) =>
            net.state.type == network.TYPE.FINAL
            &&
            ! animating
            &&
            pending === undefined
    )
    .distinctUntilChanged()
    .to( leaf.s( "canBet" ) );
    
    $resultBet
        .map( ({ bet, state }) => _.isEqual(
            state.server.shrink( bet ),
            state.server.shrink( { lines : bet.lines > 0 ? 0 : 1, multiplier : bet.multiplier } )
        ) )
        .map( doesNothing => ! doesNothing )
        .subscribe( leaf.s( "canChangeLines" ) );
    
    const $multiplierChangeDoesNothing = $resultBet
        .map( ({ bet, state }) => _.isEqual(
            state.server.shrink( bet ),
            state.server.shrink( { lines : bet.lines, multiplier : bet.multiplier > 0 ? 0 : 1 } )
        ) );
    $multiplierChangeDoesNothing
        .map( doesNothing => ! doesNothing )
        .subscribe( leaf.s( "canChangeMultiplier" ) );
    
    //max bet:
    const $canMaxBet = $resultBet
        .map( ({ bet, state }) =>
            //can increase lines:
            ( bet.lines < config.lines.length - 1 && state.server.price( bet ) <
                state.server.price( state.server.shrink( { lines : bet.lines + 1, multiplier : bet.multiplier } ) )
            )
            || //or
            //can increase mult:
            ( bet.multiplier < config.multipliers.length - 1 && state.server.price( bet ) <
                state.server.price( state.server.shrink( { lines : bet.lines, multiplier : bet.multiplier + 1 } ) )
            )
        )
        .distinctUntilChanged();
    $canMaxBet.subscribe( leaf.s( "canMaxBet" ) );
    leaf.s( "maxBet" )
        .withLatestFrom( $resultBet, ( intent, c ) => c )
        .map( ({ bet, state }) => state.server.shrink( {
            lines : config.lines.length - 1,
            multiplier : config.multipliers.length - 1
        } ) )
        .to( $userBet );
    
    //min bet:
    $canMaxBet
        .combineLatest( $multiplierChangeDoesNothing, ( c, m ) => ! c && ( ! m ) )
        .distinctUntilChanged()
        .subscribe( leaf.s( "canMinBet" ) );
    leaf.s( "minBet" )
        .withLatestFrom( $resultBet, ( m, c ) => c )
        .map( ({ bet, state }) => state.server.shrink( { lines : bet.lines, multiplier : 0 } ) )
        .to( $userBet );
    
    const $finishing = space.s( "finishing" );
    //FINISH enabled:
    $finishing
        .map( finishing => finishing == FINISHING_STATES.FINISH_INVITATION )
        .subscribe( leaf.s( "finishEnabled" ) );
    //FINISH visible:
    $finishing
        .map( finishing =>
            finishing != FINISHING_STATES.NONE
            &&
            finishing != FINISHING_STATES.RISK_INVITATION
        )
        .subscribe( leaf.s( "finishVisible" ) );
    
    //aka "slot state": true when it's "slot state":
    parent.s( "state" )
        .combineLatest( leaf.s( "isRisk" ), ( state, isRisk ) => ({ state, isRisk }) )
        .map( ({ state, isRisk }) =>
            _.includes( [ network.TYPE.FINISH, network.TYPE.FINAL, network.TYPE.REGULAR ], state.type )
            &&
            ! isRisk
        )
        .subscribe( leaf.s( "slotView" ) );
    
    parent.glue( "isRisk", leaf );
    parent.s( "state" )
        .map( state => state.type == network.TYPE.FREEGAME )
        .subscribe( leaf.s( "isFreegames" ) );
    
    parent.s( "state" )
        .map( state => state.type == network.TYPE.PRECURSOR )
        .subscribe( leaf.s( "isRoulette" ) );
    
    parent.glue( "animating", leaf );

    parent.s("slot:barrel:initiate").to(space.s("spinStart"));

    const $isProgressiveJP = space.s('isProgressiveJP', false);
    parent.s('is_progressive_jackpot').to($isProgressiveJP);

    //stop clock sound in not usual slot state
    space.s('isRisk')
        .any(space.s('isFreegames'), space.s('isJackpot'), space.s('isBonus'), space.s('isRoulette'),
            (r,f,j,b,ro) => r || f || j || b || ro)
        .filter(res => res)
        .map(() => false)
        .to(parent.s( "slot:playingClockSound" ));
    
    return space;
};





// WEBPACK FOOTER //
// ./src/library/ui.es6